import React, { useRef, useEffect, useState } from 'react';
import { SwitchWithLabel, CustomScroll, Button } from '@sumtotal/react-foundation';
import PropTypes, { checkPropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  StyledDrawer,
  NotificationHeader,
  ScrollContainer,
  HeaderContainer,
  StyledIconMenu,
  StyledMenuItem,
  StyledDivider,
  NotificationCardsDiv,
  ViewMoreButtonContainer,
  StyledDiv,
  StyledHeader
} from './notificationStyles';
import NotificationCategoryByTime from './NotificationCategoryByTime';
import {
  addIframeClickListeners,
  removeIframeClickListeners
} from '../../../modules/core/services/coreIFrameHelper';
import { getNotifications, markAllNotificationsAsRead } from '../services/notificationService';
import getLogger from '../../../utils/logger';
import useUserContext from '../../hooks/useUserContext';

function NotificationDrawer(props) {
  const { openConfig = defaultProps.openConfig, closeDrawer = defaultProps.closeDrawer } = props;
  checkPropTypes(NotificationDrawer.propTypes, props, 'prop', NotificationDrawer.name);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isUnReadOnly, setIsUnReadOnly] = useState(false);
  const [totalNotificationsCount, setTotalNotificationsCount] = useState(0);

  const wrapperRef = useRef(null);
  const { t } = useTranslation(['TRANSLATIONS_RCORE_TOPNAV']);
  const [todayData, _setTodayData] = useState([]);
  const todayDataRef = useRef(todayData);
  const setTodayData = (data) => {
    _setTodayData(data);
    todayDataRef.current = data;
  };
  const [yesterdayData, _setYesterdayData] = useState([]);
  const yesterdayDataRef = useRef(yesterdayData);
  const setYesterdayData = (data) => {
    _setYesterdayData(data);
    yesterdayDataRef.current = data;
  };
  const [olderData, _setOlderData] = useState([]);
  const olderDataRef = useRef(olderData);
  const setOlderData = (data) => {
    _setOlderData(data);
    olderDataRef.current = data;
  };
  const [pageNum, setPageNum] = useState(0);
  const [pageState, setPageState] = useState(null);
  const { user } = useUserContext();

  function getNotificationData(query, appendData) {
    if (!appendData) {
      setTodayData([]);
      setYesterdayData([]);
      setOlderData([]);
    }
    getNotifications(query)
      .then((results) => {
        if (results) {
          setTotalNotificationsCount(results.totalCount);
          setPageState(results.pageState);
          const currentDateString = new Intl.DateTimeFormat('en-US', {
            timeZone: user.userTimeZoneId,
            dateStyle: 'short',
            timeStyle: 'long'
          }).format(new Date());
          const currentDate = new Date(currentDateString).getDate();
          const currentMonth = new Date(currentDateString).getMonth();
          const currentYear = new Date(currentDateString).getFullYear();
          results.notifications.forEach((key) => {
            const notif = key;
            const NotificationDateTime = new Intl.DateTimeFormat('en-US', {
              timeZone: user.userTimeZoneId,
              dateStyle: 'short',
              timeStyle: 'long'
            }).format(new Date(`${key.createDateTime}Z`));
            const NotificationDate = new Date(NotificationDateTime).getDate();
            const NotificationMonth = new Date(NotificationDateTime).getMonth();
            const NotificationYear = new Date(NotificationDateTime).getFullYear();
            const msDiff =
              new Date(currentDateString).getTime() - new Date(NotificationDateTime).getTime();
            const days = Math.floor(msDiff / (1000 * 60 * 60 * 24));
            if (days === 0) {
              const hours = Math.floor(msDiff / (1000 * 60 * 60));
              if (hours === 0) {
                const minutes = Math.floor(msDiff / (1000 * 60));
                const minutesTag =
                  minutes === 1
                    ? 'RCORE_INAPP_NOTIFICATION_MINUTE_TAG'
                    : 'RCORE_INAPP_NOTIFICATION_MINUTES_TAG';
                notif.time = t(minutesTag, {
                  minute: minutes
                });
              } else {
                const hoursTag =
                  hours === 1
                    ? 'RCORE_INAPP_NOTIFICATION_HOUR_TAG'
                    : 'RCORE_INAPP_NOTIFICATION_HOURS_TAG';
                notif.time = t(hoursTag, {
                  hour: hours
                });
              }
            } else {
              const daysTag =
                days === 1
                  ? 'RCORE_INAPP_NOTIFICATION_DAY_TAG'
                  : 'RCORE_INAPP_NOTIFICATION_DAYS_TAG';
              notif.time = t(daysTag, {
                day: days
              });
            }
            if (
              currentYear === NotificationYear &&
              currentDate === NotificationDate &&
              currentMonth === NotificationMonth
            ) {
              setTodayData([...todayDataRef.current, notif]);
            } else if (
              currentYear === NotificationYear &&
              currentMonth === NotificationMonth &&
              currentDate - 1 === NotificationDate
            ) {
              setYesterdayData([...yesterdayDataRef.current, notif]);
            } else {
              setOlderData([...olderDataRef.current, notif]);
            }
          });
          if (appendData) {
            setTimeout(() => {
              document.getElementById(`button-${results.notifications[0].notificationId}`).focus();
            }, 200);
          }
        }
        return null;
      })
      .catch((err) => {
        getLogger().error(err.response);
      });
  }

  const markAllNotificationsAsReadMenuClick = () => {
    markAllNotificationsAsRead(user.personPK)
      .then((results) => {
        if (results) {
          if (!isUnReadOnly) {
            setTodayData(
              todayDataRef.current.map((notif) => {
                return { ...notif, isRead: true };
              })
            );
            setYesterdayData(
              yesterdayDataRef.current.map((notif) => {
                return { ...notif, isRead: true };
              })
            );
            setOlderData(
              olderDataRef.current.map((notif) => {
                return { ...notif, isRead: true };
              })
            );
          } else {
            setTodayData([]);
            setYesterdayData([]);
            setOlderData([]);
          }
        }
        return null;
      })
      .catch((err) => {
        getLogger().error(err.response);
      });
  };

  useEffect(() => {
    if (openDrawer) {
      const query = {
        personId: user.personPK,
        pageSize: 10,
        offSet: 0,
        isUnReadOnly: false,
        pageState: null
      };
      getNotificationData(query, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer]);

  useEffect(() => {
    if (openDrawer) {
      setTimeout(() => {
        document.getElementById('inapp-notification-actionMenu').focus();
      }, 200);
      document.addEventListener('click', handleClickOutside, false);
      document.addEventListener('keydown', handleFocusKey, false);
      addIframeClickListeners(handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside, false);
        document.removeEventListener('keydown', handleFocusKey, false);
        removeIframeClickListeners(handleClickOutside);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openDrawer]);

  useEffect(() => {
    setOpenDrawer(openConfig);
  }, [openConfig]);

  const handleFocusKey = (event) => {
    // Close the Drawer when focus moves out of the drawer component
    if (
      event.key === 'Tab' &&
      openDrawer &&
      wrapperRef.current &&
      !wrapperRef.current.contains(event.target)
    ) {
      cleanup();
    }
  };
  const handleClickOutside = (event) => {
    if (wrapperRef.current && !wrapperRef.current.contains(event.target) && openDrawer) {
      cleanup();
    }
  };
  const cleanup = () => {
    setTodayData([]);
    setYesterdayData([]);
    setOlderData([]);
    setOpenDrawer(false);
    setPageNum(0);
    setPageState(null);
    closeDrawer();
    setIsUnReadOnly(false);
  };
  const handleUnreadOnlySwitchChange = (event) => {
    setPageNum(0);
    setIsUnReadOnly(event.target.checked);
    const query = {
      personId: user.personPK,
      pageSize: 10,
      offSet: 0,
      pageState: null,
      isUnReadOnly: event.target.checked
    };
    getNotificationData(query, false);
  };

  const ViewMoreButtonClick = () => {
    setPageNum(pageNum + 1);
    const query = {
      personId: user.personPK,
      pageSize: 10,
      offSet: pageNum + 1,
      isUnReadOnly,
      pageState
    };
    getNotificationData(query, true);
  };

  const findIdxHelper = (notificationId, data) => {
    for (let i = 0; i < data.length; i++) {
      if (data[i].notificationId === notificationId) {
        return i;
      }
    }
  };

  const notificationReadStatusHandler = (notificationId, bucket) => {
    if (bucket === 'RCORE_INAPP_NOTIFICATION_DRAWER_TODAY_HEADER') {
      const idx = findIdxHelper(notificationId, todayDataRef.current);
      if (isUnReadOnly) todayDataRef.current.splice(idx, 1);
      else todayDataRef.current[idx].isRead = !todayDataRef.current[idx].isRead;
      setTodayData([...todayDataRef.current]);
    } else if (bucket === 'RCORE_INAPP_NOTIFICATION_DRAWER_YESTERDAY_HEADER') {
      const idx = findIdxHelper(notificationId, yesterdayDataRef.current);
      if (isUnReadOnly) yesterdayDataRef.current.splice(idx, 1);
      else yesterdayDataRef.current[idx].isRead = !yesterdayDataRef.current[idx].isRead;
      setYesterdayData([...yesterdayDataRef.current]);
    } else if (bucket === 'RCORE_INAPP_NOTIFICATION_DRAWER_OLDER_HEADER') {
      const idx = findIdxHelper(notificationId, olderDataRef.current);
      if (isUnReadOnly) olderDataRef.current.splice(idx, 1);
      else olderDataRef.current[idx].isRead = !olderDataRef.current[idx].isRead;
      setOlderData([...olderDataRef.current]);
    }
  };

  return (
    <div ref={wrapperRef}>
      <StyledDrawer anchor="top" open={openDrawer} variant="persistent">
        <HeaderContainer>
          <StyledHeader>
            <NotificationHeader>{t('RCORE_INAPP_NOTIFICATION_DRAWER_HEADER')}</NotificationHeader>
            <StyledIconMenu
              id="inapp-notification-actionMenu"
              label={t('RCORE_INAPP_NOTIFICATION_ACTION_MENU_LABEL_TEXT')}
              iconType="MaterialIcon"
              iconName="MoreVert"
              component="popper"
              role="menu"
            >
              <StyledMenuItem tabIndex={0} onClick={markAllNotificationsAsReadMenuClick}>
                <span>{t('RCORE_INAPP_NOTIFICATION_MARK_ALL_AS_READ')}</span>
              </StyledMenuItem>
            </StyledIconMenu>
          </StyledHeader>
          <StyledDivider />
        </HeaderContainer>
        <ScrollContainer>
          <CustomScroll className="StyledCustomScroll">
            <SwitchWithLabel
              label={t('RCORE_INAPP_NOTIFICATION_UNREAD_ONLY_SWITCH')}
              checked={isUnReadOnly}
              onChange={(e) => {
                handleUnreadOnlySwitchChange(e);
              }}
            />
            {todayDataRef.current.length === 0 &&
            yesterdayDataRef.current.length === 0 &&
            olderDataRef.current.length === 0 ? (
              <StyledDiv>{t('RCORE_INAPP_NOTIFICATION_NO_UNREAD_TEXT')}</StyledDiv>
            ) : (
              <>
                <NotificationCardsDiv>
                  {todayDataRef.current.length > 0 && (
                    <NotificationCategoryByTime
                      data={todayDataRef.current}
                      headerText="RCORE_INAPP_NOTIFICATION_DRAWER_TODAY_HEADER"
                      notificationReadStatusHandler={notificationReadStatusHandler}
                      closeDrawer={cleanup}
                    />
                  )}
                  {yesterdayDataRef.current.length > 0 && (
                    <NotificationCategoryByTime
                      data={yesterdayDataRef.current}
                      headerText="RCORE_INAPP_NOTIFICATION_DRAWER_YESTERDAY_HEADER"
                      notificationReadStatusHandler={notificationReadStatusHandler}
                      closeDrawer={cleanup}
                    />
                  )}
                  {olderDataRef.current.length > 0 && (
                    <NotificationCategoryByTime
                      data={olderDataRef.current}
                      headerText="RCORE_INAPP_NOTIFICATION_DRAWER_OLDER_HEADER"
                      notificationReadStatusHandler={notificationReadStatusHandler}
                      closeDrawer={cleanup}
                    />
                  )}
                </NotificationCardsDiv>
                {totalNotificationsCount -
                  (todayDataRef.current.length +
                    yesterdayDataRef.current.length +
                    olderDataRef.current.length) >
                  0 && (
                  <ViewMoreButtonContainer>
                    <Button variant="text" onClick={ViewMoreButtonClick}>
                      {t('RCORE_INAPP_NOTIFICATION_ACTION_DELETE_VIEW_MORE_BUTTON')}
                    </Button>
                  </ViewMoreButtonContainer>
                )}
              </>
            )}
          </CustomScroll>
        </ScrollContainer>
      </StyledDrawer>
    </div>
  );
}

// PropType check
NotificationDrawer.propTypes = {
  /**
   * open configuration drawer
   */
  openConfig: PropTypes.bool,
  /**
   * close configuration drawer callback function
   */
  closeDrawer: PropTypes.func
};

const defaultProps = {
  openConfig: false,
  closeDrawer: undefined
};

export default NotificationDrawer;
